import { AsyncLocalStorage } from 'node:async_hooks';
import type { CreditCostEstimate } from './credit-costs.js';

export interface CreditUsage {
  /** Sum of known credits consumed. Omitted when no called endpoint reported credits. */
  credits?: number;
  /** Number of Drishti API/MCP calls observed in this scope. */
  calls: number;
  /** Calls that did not expose credit usage metadata. */
  unknownCalls: number;
  /** Process/session total of known credits consumed. */
  sessionCredits: number;
  /** Process/session total of observed Drishti API/MCP calls. */
  sessionCalls: number;
  /** Process/session total of observed calls without credit metadata. */
  sessionUnknownCalls: number;
}

export type CreditUsageInput = number | CreditCostEstimate | undefined;

interface MutableCreditUsage {
  credits: number;
  calls: number;
  unknownCalls: number;
}

const creditScope = new AsyncLocalStorage<MutableCreditUsage>();
const sessionUsage: MutableCreditUsage = { credits: 0, calls: 0, unknownCalls: 0 };

export async function withCreditScope<T>(fn: () => Promise<T>): Promise<{ result: T; usage?: CreditUsage }> {
  const scope: MutableCreditUsage = { credits: 0, calls: 0, unknownCalls: 0 };
  const result = await creditScope.run(scope, fn);
  return {
    result,
    usage: scope.calls > 0 ? snapshot(scope) : undefined,
  };
}

export function recordCreditUsage(input?: CreditUsageInput): void {
  const scope = creditScope.getStore();
  if (!scope) return;

  scope.calls += 1;
  sessionUsage.calls += 1;

  const credits = typeof input === 'number' ? input : input?.credits;

  if (typeof credits === 'number' && Number.isFinite(credits)) {
    scope.credits += credits;
    sessionUsage.credits += credits;
  } else {
    scope.unknownCalls += 1;
    sessionUsage.unknownCalls += 1;
  }
}

export function getSessionCreditUsage(): CreditUsage | undefined {
  return sessionUsage.calls > 0 ? snapshot(sessionUsage) : undefined;
}

export function resetSessionCreditUsageForTests(): void {
  sessionUsage.credits = 0;
  sessionUsage.calls = 0;
  sessionUsage.unknownCalls = 0;
}

function snapshot(usage: MutableCreditUsage): CreditUsage {
  return {
    ...(usage.calls > usage.unknownCalls ? { credits: usage.credits } : {}),
    calls: usage.calls,
    unknownCalls: usage.unknownCalls,
    sessionCredits: sessionUsage.credits,
    sessionCalls: sessionUsage.calls,
    sessionUnknownCalls: sessionUsage.unknownCalls,
  };
}
